import React from 'react';
import { UpcomingMeetingsInfo, UpcomingMeetingInfo } from '../../types/activity';
import styles from './UpcomingMeetingsStory.module.css';

interface UpcomingMeetingsStoryProps {
  meetings: UpcomingMeetingsInfo;
  compact?: boolean;
}

/**
 * Format meeting time for display
 */
const formatMeetingTime = (meeting: UpcomingMeetingInfo, compact = false): string => {
  if (meeting.isAllDay) {
    const date = new Date(meeting.start);
    if (compact) {
      const now = new Date();
      const isToday = date.toDateString() === now.toDateString();
      const isTomorrow = date.toDateString() === new Date(now.getTime() + 86400000).toDateString();
      if (isToday) return 'Today';
      if (isTomorrow) return 'Tomorrow';
      return date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    }
    return date.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    }) + ' (All day)';
  }

  const start = new Date(meeting.start);
  const end = new Date(meeting.end);
  const now = new Date();
  
  const isToday = start.toDateString() === now.toDateString();
  const isTomorrow = start.toDateString() === new Date(now.getTime() + 86400000).toDateString();
  
  const timeStr = start.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit'
  });
  
  if (compact) {
    if (isToday) return timeStr;
    if (isTomorrow) return `Tomorrow ${timeStr}`;
    const dateStr = start.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    return `${dateStr} ${timeStr}`;
  }
  
  const endTimeStr = end.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit'
  });
  
  if (isToday) {
    return `Today ${timeStr} - ${endTimeStr}`;
  }
  if (isTomorrow) {
    return `Tomorrow ${timeStr} - ${endTimeStr}`;
  }
  
  const dateStr = start.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
  return `${dateStr}, ${timeStr} - ${endTimeStr}`;
};

export const UpcomingMeetingsStory: React.FC<UpcomingMeetingsStoryProps> = ({ meetings, compact = false }) => {
  const { meetings: meetingsList } = meetings;
  
  if (compact) {
    return (
      <div className={styles.compactContainer}>
        <span className={styles.compactIcon}>📅</span>
        <div className={styles.compactMeetings}>
          {meetingsList.map((meeting) => (
            <span key={meeting.id} className={styles.compactMeeting}>
              <span className={styles.compactTitle}>{meeting.summary}</span>
              <span className={styles.compactTime}>{formatMeetingTime(meeting, true)}</span>
            </span>
          ))}
        </div>
      </div>
    );
  }
  
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <span className={styles.icon}>📅</span>
        <span className={styles.title}>Upcoming Meetings</span>
      </div>
      <div className={styles.meetingsList}>
        {meetingsList.map((meeting, index) => (
          <div key={meeting.id} className={styles.meetingItem}>
            <div className={styles.meetingTime}>
              {formatMeetingTime(meeting)}
            </div>
            <div className={styles.meetingTitle}>
              {meeting.summary}
            </div>
            <div className={styles.meetingMeta}>
              <span className={styles.calendarEmail}>
                {meeting.calendarEmail}
              </span>
              {meeting.location && (
                <span className={styles.location}>
                  📍 {meeting.location}
                </span>
              )}
              {meeting.attendeesCount !== undefined && meeting.attendeesCount > 1 && (
                <span className={styles.attendees}>
                  👥 {meeting.attendeesCount}
                </span>
              )}
            </div>
            {index < meetingsList.length - 1 && <div className={styles.divider} />}
          </div>
        ))}
      </div>
    </div>
  );
};

export default UpcomingMeetingsStory;
